'use client';

import Image from 'next/image';
import { useId } from 'react';
import { useTranslations } from 'next-intl';
import type { PaketItem } from '@/lib/paket';
import { Modal } from '@/components/ui/modal';
import { StoreLink } from '@/components/store';
import { ProductBadge } from './product-badge';
import { SpecTable } from './spec-grid';

interface PaketItemModalProps {
  item: PaketItem;
  onClose: () => void;
}

/**
 * The quick view for one product inside a paket.
 *
 * Rendered only while a card is open, so the modal is unconditionally open —
 * `PaketIncludes` owns which child that is. Its content is already resolved on
 * the server, so this never fetches and has no loading state.
 *
 * It deliberately does not claim to show the exact article in the box: two of
 * 9010108's three children are configurable PARENTS, and the paket's own
 * description says it "kommer som standard i .308win (paketet finns i fler
 * kalibrar)". So this is the component product, described — which is what the
 * section's caption promises ("för mer information"), not a spec sheet for the
 * configured variant.
 */
export function PaketItemModal({ item, onClose }: PaketItemModalProps) {
  const t = useTranslations('product');
  const tCommon = useTranslations('common');
  const headingId = useId();

  const eyebrow = [item.brand, item.kind].filter(Boolean).join(' · ');

  return (
    <Modal isOpen onClose={onClose} labelledBy={headingId}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.1fr]">
        <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden border-b border-border bg-surface-100 p-5 sm:min-h-[380px] sm:border-b-0 sm:border-r">
          {item.image ? (
            <Image
              src={item.image.url}
              alt={item.image.label || item.name}
              fill
              className="object-contain p-5 mix-blend-multiply"
              sizes="(max-width: 640px) 100vw, 430px"
            />
          ) : (
            <span className="text-sm text-muted">{t('noImage')}</span>
          )}
        </div>

        <div className="flex flex-col p-6 sm:p-[34px]">
          <span className="mb-3.5 w-fit">
            <ProductBadge tone="accent">{t('paketModalBadge')}</ProductBadge>
          </span>

          {eyebrow && (
            <span className="font-mono text-[11.5px] uppercase tracking-[0.12em] text-foreground/50">
              {eyebrow}
            </span>
          )}

          <h2
            id={headingId}
            className="mb-3 mt-2 font-heading text-[22px] font-semibold leading-[1.15] text-heading sm:text-[27px]"
          >
            {item.name}
          </h2>

          {/* Rendered only when there is one: most children have no description
              at all, and an empty paragraph slot leaves a gap above the specs.
              Plain text, already cut to length on the server — see PaketItem. */}
          {item.description && (
            <p
              data-testid="paket-modal-description"
              className="mb-5 text-sm leading-[1.65] text-foreground/70"
            >
              {item.description}
            </p>
          )}

          {/* Returns null on an empty list, so a bare child collapses to just
              its name and the actions below. */}
          <SpecTable specs={item.specs} />

          {/* `mt-auto` pins the actions to the bottom of a tall panel; the
              margin above keeps them off the specs in a short one. */}
          <div className="mt-6 flex flex-col gap-2.5 sm:mt-auto sm:flex-row sm:pt-6">
            {/* The arrow is its own flex item rather than part of the label:
                inside the string it centred together with the text and crowded
                it, and a screen reader read out the glyph. */}
            <button
              type="button"
              onClick={onClose}
              className="flex flex-1 items-center justify-center gap-2 rounded-[3px] bg-foreground px-5 py-3.5 text-[13.5px] font-bold text-background transition-opacity hover:opacity-90"
            >
              {/* Nudged up 2px: the glyph's ink sits low in its own box, so
                  `items-center` centres the box and leaves the arrow reading
                  below the label's optical centre. A translate rather than a
                  margin, so it cannot shift the button's height. */}
              <span aria-hidden className="-translate-y-[2px] text-base leading-none">
                ←
              </span>
              <span>{t('paketBackToPackage')}</span>
            </button>
            <StoreLink
              href={`/${item.urlKey}`}
              className="rounded-[3px] border border-foreground/30 px-5 py-3.5 text-center text-[13.5px] font-semibold text-foreground transition-colors hover:border-foreground/60"
            >
              {t('paketGoToProduct')}
            </StoreLink>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={tCommon('close')}
        className="absolute right-4 top-3.5 z-[2] text-[22px] leading-none text-foreground/55 transition-colors hover:text-foreground"
      >
        ✕
      </button>
    </Modal>
  );
}
